import createCache, { type EmotionCache } from "@emotion/cache";

import logger from "~/helpers/logger";
import i18n from "~/i18n/i18n";

export interface FernieWindowHandle {
	cache: EmotionCache;
	container: HTMLElement;
	kind: "document-pip" | "popup";
	window: Window;
}

export interface FernieWindowOpenResult {
	close: () => void;
	ready: Promise<FernieWindowHandle | null>;
	window: Window | null;
}

const POPOUT_NAME = "worksuite-fernie";
const POPOUT_FEATURES = "popup=yes,width=440,height=720";
const POPOUT_URL = "/fernie-popout.html";
const POPOUT_ROOT_ID = "fernie-popout-root";
const POPOUT_READY_TIMEOUT_MS = 8000;
const STYLESHEET_LINK_LOAD_TIMEOUT_MS = 2000;

const waitForStylesheetLink = (link: HTMLLinkElement): Promise<void> =>
	new Promise((resolve) => {
		let timeoutId: ReturnType<typeof setTimeout>;

		const done = () => {
			clearTimeout(timeoutId);
			link.removeEventListener("load", done);
			link.removeEventListener("error", done);
			resolve();
		};

		link.addEventListener("load", done, { once: true });
		link.addEventListener("error", done, { once: true });
		timeoutId = setTimeout(done, STYLESHEET_LINK_LOAD_TIMEOUT_MS);
	});

// Cloned <link> stylesheets load async; await them before portal render to avoid a FOUC of raw icon ligatures.
const propagateStylesheets = (childDocument: Document): Promise<void> => {
	const pendingLinks: Promise<void>[] = [];
	document
		.querySelectorAll<HTMLLinkElement | HTMLStyleElement>('link[rel="stylesheet"], style:not([data-emotion])')
		.forEach((node) => {
			const cloned = node.cloneNode(true);
			if (cloned instanceof HTMLLinkElement) pendingLinks.push(waitForStylesheetLink(cloned));
			childDocument.head.appendChild(cloned);
		});
	return Promise.all(pendingLinks).then(() => undefined);
};

const setUpChildDocument = async (
	childWindow: Window,
	kind: FernieWindowHandle["kind"],
	backgroundColor: string
): Promise<FernieWindowHandle> => {
	const childDocument = childWindow.document;
	childDocument.title = i18n.t("fernieChat.panel.windowTitle");
	childDocument.documentElement.lang = i18n.language;
	for (const element of [childDocument.documentElement, childDocument.body]) {
		element.style.margin = "0";
		element.style.height = "100%";
		element.style.backgroundColor = backgroundColor;
	}
	let container = childDocument.getElementById(POPOUT_ROOT_ID);
	if (!container) {
		container = childDocument.createElement("div");
		container.id = POPOUT_ROOT_ID;
		childDocument.body.appendChild(container);
	}
	container.style.height = "100%";

	await propagateStylesheets(childDocument);

	const cache = createCache({ container: childDocument.head, key: "fernie-popout" });

	return { cache, container, kind, window: childWindow };
};

const isAtPopoutRoute = (childWindow: Window): boolean => {
	try {
		return childWindow.document.readyState === "complete" && childWindow.location.pathname === POPOUT_URL;
	} catch {
		return false;
	}
};

const READY_POLL_INTERVAL_MS = 50;

const readDocument = (childWindow: Window): Document | null => {
	try {
		return childWindow.document;
	} catch {
		return null;
	}
};

// A reused named window serves its previous document — still "complete" at the popout path — for the whole of the new fetch, so only a swapped Document object proves the navigation landed.
const waitForPopoutReady = (childWindow: Window, backgroundColor: string): Promise<FernieWindowHandle | null> =>
	new Promise((resolve) => {
		const deadline = Date.now() + POPOUT_READY_TIMEOUT_MS;
		const documentBeforeNavigation = readDocument(childWindow);

		const tick = () => {
			if (childWindow.closed) {
				resolve(null);
				return;
			}

			const hasNavigated = readDocument(childWindow) !== documentBeforeNavigation;
			if (hasNavigated && isAtPopoutRoute(childWindow)) {
				setUpChildDocument(childWindow, "popup", backgroundColor)
					.then(resolve)
					.catch((e) => {
						logger.error("openFernieWindow: failed to set up pop-out document", e);
						resolve(null);
					});
				return;
			}

			if (Date.now() >= deadline) {
				resolve(null);
				return;
			}

			setTimeout(tick, READY_POLL_INTERVAL_MS);
		};

		tick();
	});

const openFernieWindow = (backgroundColor: string): FernieWindowOpenResult => {
	let childWindow: Window | null = null;
	let cancelled = false;

	const close = () => {
		cancelled = true;
		try {
			childWindow?.close();
		} catch (error) {
			logger.error("openFernieWindow: failed to close window", error);
		}
	};

	const openPopup = (): Promise<FernieWindowHandle | null> => {
		if (cancelled) return Promise.resolve(null);
		try {
			childWindow = window.open(POPOUT_URL, POPOUT_NAME, POPOUT_FEATURES);
		} catch (error) {
			logger.error("openFernieWindow: failed to open pop-out window", error);
		}
		return childWindow ? waitForPopoutReady(childWindow, backgroundColor) : Promise.resolve(null);
	};

	const openPictureInPicture = async (): Promise<FernieWindowHandle | null> => {
		try {
			const pip = window.documentPictureInPicture;
			if (!pip) return openPopup();
			// requestWindow consumes transient activation, so it must run before the first await.
			childWindow = pip.window ?? (await pip.requestWindow({ disallowReturnToOpener: true, height: 680, width: 420 }));
			if (cancelled) {
				close();
				return null;
			}
			return await setUpChildDocument(childWindow, "document-pip", backgroundColor);
		} catch (error) {
			logger.error("openFernieWindow: picture-in-picture unavailable", error);
			childWindow?.close();
			childWindow = null;
			return openPopup();
		}
	};

	return {
		close,
		ready: openPictureInPicture(),
		get window() {
			return childWindow;
		}
	};
};

export default openFernieWindow;
